/**
 * The v3.0 renaming of the `ORG_*` columns to `LOC_*`.
 *
 * Mirrors the alias registry in `CsvFieldIndices`. Both spellings are accepted on input, so a rule
 * whose `primaryField` still says `ORG_CITY` is not broken — it is the v2.0 name for a column a
 * v3.0 file calls `LOC_CITY`. Without showing both, an admin reading the rules list against a v3.0
 * submission sees column names that appear in no file they have.
 *
 * The rename is a rename only: the column keeps its position and its meaning. What changed is the
 * concept it belongs to — under SOW #4 these describe the *Location*, not the Organization, which
 * is now derived from the endpoint certificate rather than from the CSV.
 *
 * @date: 08/12/26
 * @copy: (C) Copyright 2026 MaxMD Corporation, All Rights Reserved.
 */

export const OrgToLocAliases = {
    ORG_UID: 'LOC_UID',
    ORG_NAME: 'LOC_NAME',
    ORG_NPI: 'LOC_NPI',
    ORG_ADDR_1: 'LOC_ADDR_1',
    ORG_ADDR_2: 'LOC_ADDR_2',
    ORG_CITY: 'LOC_CITY',
    ORG_STATE: 'LOC_STATE',
    ORG_ZIP: 'LOC_ZIP',
    ORG_COUNTRY: 'LOC_COUNTRY',
    ORG_PHONE: 'LOC_PHONE',
    ORG_FAX: 'LOC_FAX',
    ORG_EMAIL: 'LOC_EMAIL',
    ORG_LAT: 'LOC_LAT',
    ORG_LONG: 'LOC_LONG',
    ORG_GEO_ADDR_TYPE: 'LOC_GEO_ADDR_TYPE',
    ORG_SPEC_CODE: 'LOC_SPEC_CODE',
    ORG_SPEC_TEXT: 'LOC_SPEC_TEXT',
    ORG_TYPE: 'LOC_TYPE',
    ORG_HCSC: 'LOC_HCSC',
    ORG_INFO: 'LOC_INFO',
    ORG_SPECCAT_CODE: 'LOC_SPECCAT_CODE',
    ORG_SPECCAT_TEXT: 'LOC_SPECCAT_TEXT',
};

/** The v3.0 name of a column, or null when the column was not renamed. */
export function v3NameOf(column) {
    if (!column) {
        return null;
    }
    return OrgToLocAliases[String(column).trim().toUpperCase()] ?? null;
}

/**
 * How a column should read in the admin UI: `ORG_CITY → LOC_CITY` for renamed columns, the plain
 * name otherwise.
 */
export function columnLabel(column) {
    const v3 = v3NameOf(column);
    return v3 ? `${column} → ${v3}` : column;
}
